/**
 * Learning insights panel (B3-c). Surfaces the Org Brain learning loop's
 * observations about automation rules — high performers, underused rules,
 * and failing rules — so admins can tune their automation.
 */

import { Button, Card, Spinner } from '@centraoneplus/ui';
import { LearningInsight, useOrgBrainInsights } from './hooks';

interface Props {
  tenantId: string;
}

const KIND_LABEL: Record<string, string> = {
  high_performer: 'High performer',
  underused_rule: 'Underused',
  failing_rule: 'Failing',
};

const SEV_STYLES: Record<string, string> = {
  breaking: 'bg-red-100 text-red-800',
  warning: 'bg-amber-100 text-amber-800',
  info: 'bg-sky-100 text-sky-800',
};

export default function OrgBrainInsights({ tenantId }: Props) {
  const insights = useOrgBrainInsights(tenantId);
  const report = insights.data;

  return (
    <Card className="p-6 space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Automation insights</h2>
          <p className="text-sm text-gray-600 mt-1 max-w-xl">
            What the Org Brain has learned from how your automation rules
            actually perform — so you can retire the noise and fix the
            failures.
          </p>
        </div>
        <Button onClick={() => insights.mutate()} disabled={insights.isPending}>
          {insights.isPending ? 'Analyzing…' : 'Generate insights'}
        </Button>
      </header>

      {insights.isPending && <Spinner />}

      {report && !insights.isPending && (
        report.insights.length === 0 ? (
          <div className="text-sm text-gray-700 bg-gray-50 rounded px-3 py-2">
            No insights yet. Insights build up as your automation rules run —
            check back after there's some execution history.
          </div>
        ) : (
          <ul className="space-y-2">
            {report.insights.map((ins: LearningInsight, i: number) => (
              <li
                key={`${ins.kind}-${i}`}
                className="border border-gray-200 rounded p-3"
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-block px-2 py-0.5 text-xs rounded font-medium ${
                      SEV_STYLES[ins.severity] ?? 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {KIND_LABEL[ins.kind] ?? ins.kind}
                  </span>
                </div>
                <div className="text-sm text-gray-900 mt-1">{ins.message}</div>
              </li>
            ))}
          </ul>
        )
      )}

      {insights.error && (
        <div className="text-sm text-red-700 bg-red-50 rounded px-3 py-2">
          {(insights.error as any)?.response?.data?.message ||
            (insights.error as any)?.message ||
            'Could not generate insights'}
        </div>
      )}
    </Card>
  );
}